import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  EntryLabelPrintControl,
  type LabelPrintShow,
} from '@/components/entries/EntryLabelPrintControl'
import type { CheckedInExhibit } from '@/hooks/useEntryIntake'
import { formatCheckInTimestamp } from '@/features/intake/utils'

type JobStatus =
  | { status: 'pending'; errorMessage?: string }
  | { status: 'success'; errorMessage?: string }
  | { status: 'error'; errorMessage?: string }

type RecentCheckInsSectionProps = {
  events: CheckedInExhibit[]
  collapsed: boolean
  onToggleCollapsed: () => void
  listId: string
  showSyncNowPrompt: boolean
  onSyncPending: () => void
  syncDisabled: boolean
  syncDisabledReason?: string
  getJobStatus?: (entryId: string) => JobStatus | undefined
  labelPrintShow?: LabelPrintShow
  onCopyExhibitId?: (entryCode: string | null | undefined) => void
  onOpenEntry?: (event: CheckedInExhibit) => void
  disableOpenEntry?: boolean
}

export function RecentCheckInsSection({
  events,
  collapsed,
  onToggleCollapsed,
  listId,
  showSyncNowPrompt,
  onSyncPending,
  syncDisabled,
  syncDisabledReason,
  getJobStatus,
  labelPrintShow,
  onCopyExhibitId,
  onOpenEntry,
  disableOpenEntry = false,
}: RecentCheckInsSectionProps) {
  if (events.length === 0) {
    return null
  }

  return (
    <section className="rounded-md border bg-card shadow-sm" data-testid="recent-checkins">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex flex-col">
          <span className="text-sm font-medium">Recent check-ins</span>
          <span className="text-xs text-muted-foreground">
            Showing {events.length} latest check-ins on this workstation.
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showSyncNowPrompt && (
            <Button
              size="sm"
              onClick={onSyncPending}
              disabled={syncDisabled}
              title={syncDisabledReason}
              data-testid="recent-sync-now"
            >
              Sync now
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={onToggleCollapsed}
            aria-expanded={!collapsed}
            aria-controls={listId}
          >
            {collapsed ? 'Show' : 'Hide'}
          </Button>
        </div>
      </div>
      {!collapsed && (
        <ul className="divide-y border-t" id={listId}>
          {events.map(event => {
            const jobStatus =
              labelPrintShow && event.entryId && getJobStatus
                ? getJobStatus(event.entryId)
                : undefined
            const isFailedJob = jobStatus?.status === 'error'
            const displayClassName = event.className ?? 'Class pending'
            const displayClassCode = event.classCode ?? null
            const exhibitorLabel = event.exhibitorName ?? 'Exhibitor pending'
            const statusBadgeVariant =
              jobStatus?.status === 'success'
                ? 'secondary'
                : jobStatus?.status === 'error'
                  ? 'destructive'
                  : 'outline'
            const statusBadgeText =
              jobStatus?.status === 'success'
                ? 'Printed'
                : jobStatus?.status === 'error'
                  ? 'Failed'
                  : jobStatus?.status === 'pending'
                    ? 'Printing…'
                    : null
            return (
              <li
                key={`${event.entryId}-${event.exhibitId}`}
                className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between"
              >
                <div className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold leading-tight">{exhibitorLabel}</span>
                    {event.checkedInAt && (
                      <span className="text-xs text-muted-foreground">
                        {formatCheckInTimestamp(event.checkedInAt)}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-mono text-sm">{event.entryCode || 'Pending'}</span>
                    {event.remappedFrom && (
                      <span className="text-xs font-mono text-muted-foreground">
                        Remapped {event.remappedFrom} → {event.entryCode}
                      </span>
                    )}
                    {statusBadgeText && (
                      <Badge variant={statusBadgeVariant} className="text-[11px] uppercase">
                        {statusBadgeText}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {displayClassCode && <span className="font-mono">{displayClassCode}</span>}
                    <span>{displayClassName}</span>
                  </div>
                  {isFailedJob && jobStatus?.errorMessage && (
                    <p className="text-xs text-destructive">
                      Last attempt failed: {jobStatus.errorMessage}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {labelPrintShow && event.entryId && (
                    <EntryLabelPrintControl
                      entry={{
                        id: event.entryId,
                        entryCode: event.entryCode,
                        exhibitId: event.exhibitId,
                        status: 'checked_in',
                        class: event.classId
                          ? {
                              id: event.classId,
                              code: event.classCode ?? '',
                              name: event.className ?? '',
                            }
                          : null,
                      }}
                      show={labelPrintShow}
                    />
                  )}
                  {onCopyExhibitId && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onCopyExhibitId(event.entryCode)}
                      disabled={!event.entryCode}
                    >
                      Copy Exhibit ID
                    </Button>
                  )}
                  {onOpenEntry && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onOpenEntry(event)}
                      disabled={disableOpenEntry}
                    >
                      Open entry
                    </Button>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
